// Slash-command autocomplete list. Shown above the composer when the
// user types `/name` at the start of the input. Visual language matches
// the attachment menu / mention picker (flat card, 8px rows, indigo
// highlight on the active row). Keyboard navigation is driven by the
// composer (Arrow / Enter / Tab / Esc) via `selectedIndex`; this
// component also commits on tap.

import React from "react";
import { Pressable, Text, View } from "react-native";
import { Slash } from "lucide-react-native";
import { useColorScheme } from "../../foundations/colorScheme";
import { tokens } from "../../design-system/tokens";
import type { SlashCommand } from "./slashCommands";

export interface SlashCommandPickerProps {
  readonly commands: readonly SlashCommand[];
  readonly selectedIndex: number;
  readonly onSelect: (command: SlashCommand) => void;
  readonly testID?: string;
}

export const SlashCommandPicker = React.memo<SlashCommandPickerProps>(
  ({ commands, selectedIndex, onSelect, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: "light" | "dark" = scheme === "dark" ? "dark" : "light";
    const colors = tokens.color.semantic[colorScheme];

    if (commands.length === 0) return null;

    return (
      <View
        accessibilityRole="menu"
        accessibilityLabel="Slash commands"
        className="self-start min-w-[280px] max-w-[420px] rounded-[12px] p-1 gap-1 bg-light-surface-primary dark:bg-[#15171C] border border-light-border-secondary dark:border-white/[0.08]"
        testID={testID}
      >
        {commands.map((command, index) => {
          const active = index === selectedIndex;
          return (
            <Pressable
              key={command.name}
              accessibilityRole="menuitem"
              accessibilityState={{ selected: active }}
              onPress={() => onSelect(command)}
              className={`
                flex flex-row items-center gap-3 px-2 py-2 rounded-lg
                ${active ? "bg-light-accent-indigo/10 dark:bg-[#537EFF]/15" : ""}
              `}
              testID={`slash-command-${command.name}`}
            >
              <Slash
                size={16}
                strokeWidth={2}
                color={active ? colors.accent.indigo : colors.text.secondary}
              />
              <View className="flex-1 min-w-0 flex-col">
                <View className="flex-row items-baseline gap-2">
                  <Text
                    className="font-sans text-[14px] font-semibold text-light-text-primary dark:text-[#F5F6F8]"
                  >
                    /{command.name}
                  </Text>
                  {command.argsHint !== undefined ? (
                    <Text
                      numberOfLines={1}
                      className="font-sans text-[12px] font-normal text-light-text-secondary dark:text-[#8B8F97]"
                    >
                      {command.argsHint}
                    </Text>
                  ) : null}
                </View>
                <Text
                  numberOfLines={1}
                  className="font-sans text-[12px] leading-4 font-normal text-light-text-secondary dark:text-[#B5BAC3]"
                >
                  {command.description}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>
    );
  },
);

SlashCommandPicker.displayName = "SlashCommandPicker";
